import React, { useEffect, useState, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const SERVER_IP = "152.228.128.172";

function StreamHub() {
    const [streams, setStreams] = useState([]);

    const updateStreams = async () => {
        try {
            const res = await fetch(`https://theviolentsquirrels.fr/api/paths/list`);
            const data = await res.json();
            const activeStreams = (data.items || [])
                .filter(item => item.ready === true)
                .map(item => item.name);
            setStreams(activeStreams);
        } catch (err) {
            console.error("Erreur API:", err);
        }
    };

    useEffect(() => {
        updateStreams();
        const interval = setInterval(updateStreams, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div style={styles.container}>
            <div style={styles.headerGlow} />
            <header style={styles.header} className="fade-in-up">
                <h1 style={styles.title} className="tvs-title-gradient">🐿️ SquirrelTV</h1>
                <div style={{
                    ...styles.badge,
                    ...(streams.length === 0 ? styles.badgeOffline : {}),
                }}>
                    {streams.length > 0 && <span style={styles.badgeDot} />}
                    {streams.length} {streams.length > 1 ? 'STREAMS EN LIGNE' : 'STREAM EN LIGNE'}
                </div>
            </header>

            {/* LA GRILLE COMMENCE ICI */}
            <div style={styles.grid}>
                {streams.length > 0 ? (
                    streams.map((name, i) => (
                        <div
                            key={name}
                            style={{ ...styles.streamCard, animationDelay: `${Math.min(i, 8) * 70}ms` }}
                            className="fade-in-up tvs-card-hover"
                        >
                            <div style={styles.cardHeader}>
                                <span style={styles.dot}></span>
                                <span style={styles.streamerName}>{name}</span>
                            </div>
                            <VideoPlayer src={`https://theviolentsquirrels.fr/hls/${name}/index.m3u8`} />
                        </div>
                    ))
                ) : (
                    <div style={styles.noStream} className="fade-in-up">
                        <div style={styles.noStreamIcon}>🐿️</div>
                        <p style={styles.noStreamText}>En attente de diffuseurs...</p>
                        <p style={styles.noStreamHint}>Reviens un peu plus tard, ou lance ton propre stream !</p>
                    </div>
                )}
            </div>
        </div>
    );
}

const VideoPlayer = ({ src }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const stallRef = useRef({ count: 0, duration: 0, start: null });
    const [showDebug, setShowDebug] = useState(false);
    const [debugStats, setDebugStats] = useState(null);

    useEffect(() => {
        // Le setTimeout permet d'attendre que React ait fini de rendre le HTML
        const timer = setTimeout(() => {
            if (videoRef.current && !playerRef.current) {
                const player = videojs(videoRef.current, {
                    autoplay: false,
                    controls: true,
                    responsive: true,
                    fluid: true,
                    liveui: true,
                    sources: [{ src, type: 'application/x-mpegURL' }]
                });
                playerRef.current = player;

                player.on('waiting', () => {
                    stallRef.current.count += 1;
                    stallRef.current.start = performance.now();
                });
                player.on('playing', () => {
                    if (stallRef.current.start) {
                        stallRef.current.duration += (performance.now() - stallRef.current.start) / 1000;
                        stallRef.current.start = null;
                    }
                });
            }
        }, 0);

        return () => {
            clearTimeout(timer);
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [src]);

    useEffect(() => {
        if (!showDebug) return;

        const interval = setInterval(() => {
            const player = playerRef.current;
            if (!player) return;

            const tech = player.tech && player.tech(true);
            const videoEl = tech && tech.el && tech.el();
            const vhsStats = tech && tech.vhs && tech.vhs.stats;

            let dropped = 0, total = 0;
            if (videoEl && videoEl.getVideoPlaybackQuality) {
                const q = videoEl.getVideoPlaybackQuality();
                dropped = q.droppedVideoFrames || 0;
                total = q.totalVideoFrames || 0;
            }

            let bufferAhead = 0;
            try {
                const buffered = player.buffered();
                if (buffered && buffered.length) {
                    bufferAhead = buffered.end(buffered.length - 1) - player.currentTime();
                }
            } catch (e) { /* pas de buffer dispo */ }

            let stallDuration = stallRef.current.duration;
            if (stallRef.current.start) {
                stallDuration += (performance.now() - stallRef.current.start) / 1000;
            }

            setDebugStats({
                bandwidth: vhsStats ? vhsStats.bandwidth : null,
                mediaBytesTransferred: vhsStats ? vhsStats.mediaBytesTransferred : null,
                mediaRequestsErrored: vhsStats ? vhsStats.mediaRequestsErrored : null,
                bufferAhead,
                dropped,
                total,
                stallCount: stallRef.current.count,
                stallDuration,
                resolution: videoEl && videoEl.videoWidth ? `${videoEl.videoWidth}x${videoEl.videoHeight}` : '—',
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [showDebug]);

    return (
        <div className="video-container" style={{ position: 'relative' }}>
            <button
                style={{ ...styles.debugToggle, ...(showDebug ? styles.debugToggleActive : {}) }}
                className="tvs-btn-lift"
                onClick={() => setShowDebug(v => !v)}
                title="Debug stream"
            >
                ⓘ
            </button>

            {showDebug && (
                <div style={styles.debugPanel} className="panel-pop">
                    <div style={styles.debugHeader}>
                        <span style={styles.debugDot} />
                        <span style={styles.debugTitle}>Debug stream</span>
                    </div>
                    {debugStats ? (
                        <div style={styles.debugRows}>
                            <DebugRow label="Bande passante" value={debugStats.bandwidth ? `${(debugStats.bandwidth / 1000).toFixed(0)} kbps` : '—'} />
                            <DebugRow label="Buffer d'avance" value={`${debugStats.bufferAhead.toFixed(1)}s`} />
                            <DebugRow
                                label="Frames perdues"
                                value={`${debugStats.dropped}/${debugStats.total}${debugStats.total ? ` (${((debugStats.dropped / debugStats.total) * 100).toFixed(1)}%)` : ''}`}
                            />
                            <DebugRow label="Stalls" value={`${debugStats.stallCount} — ${debugStats.stallDuration.toFixed(1)}s cumulées`} />
                            <DebugRow label="Résolution" value={debugStats.resolution} />
                            <DebugRow label="Data reçue" value={debugStats.mediaBytesTransferred ? `${(debugStats.mediaBytesTransferred / 1024 / 1024).toFixed(1)} MB` : '—'} />
                            {debugStats.mediaRequestsErrored > 0 && (
                                <DebugRow label="Requêtes en erreur" value={debugStats.mediaRequestsErrored} alert />
                            )}
                        </div>
                    ) : (
                        <div style={styles.debugLoading}>Chargement des stats...</div>
                    )}
                </div>
            )}

            <div data-vjs-player>
                <video ref={videoRef} className="video-js vjs-big-play-centered" />
            </div>
        </div>
    );
};

const DebugRow = ({ label, value, alert }) => (
    <div style={styles.debugRow}>
        <span style={styles.debugLabel}>{label}</span>
        <span style={alert ? styles.debugValueAlert : styles.debugValue}>{value}</span>
    </div>
);

export default StreamHub;

const styles = {
    container: {
        backgroundColor: '#0a0a0a',
        minHeight: '100vh',
        padding: '20px',
        fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        color: 'white',
        position: 'relative',
        overflowX: 'hidden'
    },
    headerGlow: {
        position: 'absolute',
        top: '-100px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '520px',
        height: '280px',
        background: 'radial-gradient(circle, rgba(255,68,68,0.22) 0%, rgba(255,68,68,0) 70%)',
        animation: 'heroGlow 4s ease-in-out infinite',
        pointerEvents: 'none'
    },
    header: {
        textAlign: 'center',
        marginBottom: '40px',
        borderBottom: '1px solid #222',
        paddingBottom: '20px',
        position: 'relative'
    },
    title: {
        fontSize: 'clamp(1.6rem, 5vw, 2.3rem)',
        fontWeight: 800,
        letterSpacing: '1px',
        margin: '0 0 12px 0'
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        background: 'linear-gradient(135deg, #ff6b6b, #ff4444)',
        padding: '6px 16px',
        borderRadius: '20px',
        fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
        fontWeight: 'bold',
        boxShadow: '0 4px 16px rgba(255,68,68,0.35)'
    },
    badgeOffline: {
        background: '#222',
        boxShadow: 'none',
        color: '#888'
    },
    badgeDot: {
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        backgroundColor: 'white',
        animation: 'pulse 2s infinite'
    },
    grid: {
        display: 'grid',
        // Responsive: 1 colonne sur mobile, 2 sur tablette, jusqu'à 3 sur desktop
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
        gap: '20px',
        maxWidth: '1600px',
        margin: '0 auto',
        position: 'relative'
    },
    streamCard: {
        backgroundColor: '#161616',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid #333',
        boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
    },
    cardHeader: {
        padding: '12px 15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'linear-gradient(to right, #161616, #222)'
    },
    dot: {
        width: '10px',
        height: '10px',
        backgroundColor: '#ff4444',
        borderRadius: '50%',
        boxShadow: '0 0 8px #ff4444',
        animation: 'pulse 2s infinite'
    },
    streamerName: {
        fontWeight: 'bold',
        fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
        textTransform: 'capitalize'
    },
    noStream: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: 'clamp(50px, 15vw, 100px) 20px',
        color: '#666'
    },
    noStreamIcon: {
        fontSize: '2.5rem',
        marginBottom: '14px',
        opacity: 0.6
    },
    noStreamText: {
        fontSize: 'clamp(1rem, 3vw, 1.4rem)',
        color: '#999',
        margin: '0 0 6px 0',
        fontWeight: 600
    },
    noStreamHint: {
        fontSize: '0.85rem',
        color: '#555',
        margin: 0
    },
    debugToggle: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 10,
        background: 'rgba(0,0,0,0.45)',
        border: '1px solid rgba(255,255,255,0.2)',
        color: 'rgba(255,255,255,0.6)',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        fontSize: '0.8rem',
        lineHeight: 1,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.6
    },
    debugToggleActive: {
        background: 'rgba(255,68,68,0.25)',
        border: '1px solid #ff4444',
        color: 'white',
        opacity: 1
    },
    debugPanel: {
        position: 'absolute',
        top: '38px',
        right: '8px',
        zIndex: 10,
        background: 'rgba(14,14,14,0.92)',
        backdropFilter: 'blur(6px)',
        border: '1px solid #333',
        borderLeft: '3px solid #ff4444',
        borderRadius: '10px',
        padding: '12px 14px',
        fontSize: '0.72rem',
        fontFamily: 'monospace',
        color: '#ccc',
        whiteSpace: 'nowrap',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
    },
    debugHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        marginBottom: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #2a2a2a'
    },
    debugDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#ff4444',
        animation: 'pulse 2s infinite'
    },
    debugTitle: {
        fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 'bold',
        fontSize: '0.72rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#ff8888'
    },
    debugRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    debugRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '14px'
    },
    debugLabel: {
        color: '#888'
    },
    debugValue: {
        color: '#eee'
    },
    debugValueAlert: {
        color: '#ff6b6b',
        fontWeight: 'bold'
    },
    debugLoading: {
        color: '#777'
    }
};